import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { api } from '../../Api';
import { Store } from '../../Store';
import { getError } from '../../utils';
import { checkBridge, captureFinger, verifyFinger } from '../../utils/fingerprintBridge';
import '../../assets/ops.css';

// The delivery counter.
//
// One screen, one job: the customer is standing here, prove who they are and
// hand over the exact units. Search by username → pick what they came for →
// OTP → fingerprint → scan the stickers → done. Every step is switchable from
// the Developer page, and the wizard silently drops any step that is off.

export default function DeliveryScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [requests, setRequests] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [queue, setQueue] = useState(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${userInfo?.token}` }),
    [userInfo]
  );

  // --- search ---------------------------------------------------------------
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${api}/api/delivery/admin/search`, {
          headers,
          params: { q: term },
        });
        setResults(data.users || []);
      } catch (err) {
        toast.error(getError(err));
      } finally {
        setSearching(false);
      }
    }, 320);
    return () => clearTimeout(timer);
  }, [query, headers]);

  const loadRequests = useCallback(
    async (userId) => {
      try {
        const { data } = await axios.get(`${api}/api/delivery/admin/requests`, {
          headers,
          params: { userId, open: 'true', pageSize: 50 },
        });
        setRequests(data.rows || []);
        return data.rows || [];
      } catch (err) {
        toast.error(getError(err));
        return [];
      }
    },
    [headers]
  );

  // Waiting queue, so the counter can see who is next without searching.
  const loadQueue = useCallback(async () => {
    try {
      const { data } = await axios.get(`${api}/api/delivery/admin/requests`, {
        headers,
        params: { open: 'true', pageSize: 25 },
      });
      setQueue(data);
    } catch {
      /* the queue is a convenience — never block the counter on it */
    }
  }, [headers]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const pickUser = async (user) => {
    setSelectedUser(user);
    setQuery('');
    setResults([]);
    const rows = await loadRequests(user._id);
    // One thing waiting? Open it straight away — saves a click every time.
    if (rows.length === 1) setActiveId(rows[0]._id);
  };

  const reset = () => {
    setSelectedUser(null);
    setRequests([]);
    setActiveId(null);
    loadQueue();
  };

  return (
    <div className="ops">
      <Helmet><title>Delivery Counter</title></Helmet>

      <div className="ops-head">
        <div>
          <h2>Delivery Counter</h2>
          <p>
            Search for the customer standing at the counter, verify them, scan the units
            and hand them over. Every step is recorded.
          </p>
        </div>
        {selectedUser && (
          <button className="ops-btn" onClick={reset}>
            <i className="fa fa-arrow-left" /> New customer
          </button>
        )}
      </div>

      {!selectedUser ? (
        <>
          <div className="ops-card">
            <h3>Find the customer</h3>
            <p className="sub">Search by username, name, phone, or the request code they show you.</p>
            <div style={{ position: 'relative' }}>
              <input
                className="ops-input"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type at least 2 characters…"
                style={{ paddingLeft: 38 }}
              />
              <i
                className={`fa ${searching ? 'fa-spinner fa-spin' : 'fa-search'}`}
                style={{ position: 'absolute', left: 14, top: 13, color: 'var(--ops-muted)' }}
              />
            </div>

            {query.trim().length >= 2 && (
              <div className="ops-rows" style={{ marginTop: 14 }}>
                {results.length === 0 && !searching ? (
                  <div className="ops-empty" style={{ padding: 26 }}>
                    <i className="fa fa-user-times" />
                    Nobody with an uncollected request matches “{query.trim()}”.
                  </div>
                ) : (
                  results.map((user) => (
                    <div key={user._id} className="ops-row" onClick={() => pickUser(user)}>
                      <div className="main">
                        <strong>{user.username}</strong>
                        <span>{user.fullName}{user.phone ? ` · ${user.phone}` : ''}</span>
                      </div>
                      <div className="side">
                        <span className="ops-badge blue">
                          {user.units} unit{user.units === 1 ? '' : 's'}
                        </span>
                        <div style={{ fontSize: 11, color: 'var(--ops-muted)', marginTop: 4 }}>
                          {user.requests} request{user.requests === 1 ? '' : 's'}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <WaitingQueue queue={queue} onPick={(row) => pickUser({ _id: row.user, username: row.username, fullName: row.userFullName, phone: row.userPhone })} />
        </>
      ) : (
        <>
          <div className="ops-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div
                style={{
                  width: 46, height: 46, borderRadius: '50%',
                  background: '#dbeafe', color: '#1e40af',
                  display: 'grid', placeItems: 'center',
                  fontWeight: 700, fontSize: 18, flex: 'none',
                }}
              >
                {(selectedUser.username || '?').slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: 0 }}>{selectedUser.username}</h3>
                <p className="sub" style={{ margin: 0 }}>
                  {selectedUser.fullName}{selectedUser.phone ? ` · ${selectedUser.phone}` : ''}
                </p>
              </div>
              <span className="ops-badge blue">
                {requests.length} waiting
              </span>
            </div>
          </div>

          {requests.length === 0 ? (
            <div className="ops-empty">
              <i className="fa fa-check-circle" />
              Nothing left to hand over for this customer.
            </div>
          ) : (
            <div className="ops-rows" style={{ marginBottom: 16 }}>
              {requests.map((request) => (
                <div
                  key={request._id}
                  className={`ops-row ${activeId === request._id ? 'selected' : ''}`}
                  onClick={() => setActiveId(activeId === request._id ? null : request._id)}
                >
                  {request.productImage && <img className="ops-thumb" src={request.productImage} alt="" />}
                  <div className="main">
                    <strong>{request.quantity} × {request.productName}</strong>
                    <span>
                      {request.code} · asked {new Date(request.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="side">
                    <StatusBadge request={request} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeId && (
            <HandoverWizard
              key={activeId}
              requestId={activeId}
              headers={headers}
              onDone={async () => {
                const rows = await loadRequests(selectedUser._id);
                setActiveId(rows.length ? rows[0]._id : null);
                if (!rows.length) toast.success('Everything for this customer is handed over.');
                loadQueue();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

function StatusBadge({ request }) {
  const v = request.verification || {};
  if (request.status === 'pending') return <span className="ops-badge grey">Waiting</span>;
  if (v.overridden) return <span className="ops-badge amber">Overridden</span>;
  if (v.fingerprintVerified) return <span className="ops-badge green">Verified</span>;
  if (v.otpVerified) return <span className="ops-badge blue">OTP done</span>;
  return <span className="ops-badge amber">In progress</span>;
}

// ---------------------------------------------------------------------------
// The queue of everyone waiting
// ---------------------------------------------------------------------------
function WaitingQueue({ queue, onPick }) {
  if (!queue) return null;
  const rows = queue.rows || [];
  return (
    <div className="ops-card">
      <h3>Waiting to collect</h3>
      <p className="sub">
        {rows.length ? `${queue.total} open request(s), oldest first.` : 'Nobody is waiting right now.'}
      </p>
      {rows.length > 0 && (
        <div className="ops-table-wrap">
          <table className="ops-table" style={{ minWidth: 640 }}>
            <thead>
              <tr>
                <th>Request</th>
                <th>Customer</th>
                <th>Item</th>
                <th className="num">Qty</th>
                <th className="ops-hide-sm">Asked</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {[...rows].reverse().map((row) => (
                <tr key={row._id}>
                  <td className="ops-mono">{row.code}</td>
                  <td><strong>{row.username}</strong></td>
                  <td>{row.productName}</td>
                  <td className="num"><strong>{row.quantity}</strong></td>
                  <td className="ops-hide-sm" style={{ fontSize: 12, color: 'var(--ops-muted)' }}>
                    {new Date(row.createdAt).toLocaleString()}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="ops-btn sm primary" onClick={() => onPick(row)}>
                      Serve
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The hand-over wizard
// ---------------------------------------------------------------------------
function HandoverWizard({ requestId, headers, onDone }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [showQuantity, setShowQuantity] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${api}/api/delivery/admin/request/${requestId}`, { headers });
      setDetail(data);
      return data;
    } catch (err) {
      toast.error(getError(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, [requestId, headers]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="ops-empty"><i className="fa fa-spinner fa-spin" />Loading request…</div>;
  }
  if (!detail) return null;

  const { request, config, product, fingerprint, bridge, availableUnits } = detail;
  const v = request.verification || {};

  // Which steps apply, in order. A step that is switched off never appears.
  const steps = [];
  if (config.handoverOtpEnabled) {
    steps.push({ key: 'otp', title: 'Verify OTP', hint: 'Code sent to the customer', done: v.otpVerified });
  }
  if (config.requireFingerprint) {
    steps.push({
      key: 'fingerprint',
      title: 'Fingerprint',
      hint: v.overridden ? 'Overridden' : 'Scan to confirm identity',
      done: v.fingerprintVerified || v.overridden,
    });
  }
  if (config.requireSerialScan) {
    steps.push({
      key: 'scan',
      title: 'Scan units',
      hint: `${request.scannedSerials.length} of ${request.quantity}`,
      done: request.scannedSerials.length >= request.quantity,
    });
  }
  steps.push({ key: 'confirm', title: 'Hand over', hint: 'Complete the delivery', done: false });

  const currentIndex = Math.max(steps.findIndex((s) => !s.done), 0);
  const current = steps[currentIndex];
  const notEnoughStock = availableUnits < request.quantity;

  return (
    <div className="ops-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0 }}>
            {request.quantity} × {request.productName}
          </h3>
          <p className="sub" style={{ margin: '3px 0 0' }}>
            {request.code}
            {request.originalQuantity !== request.quantity && ` · originally ${request.originalQuantity}`}
            {detail.line && ` · ${detail.line.delivered} of ${detail.line.quantity} collected so far`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {config.allowAdminEditQuantity && (
            <button className="ops-btn sm" onClick={() => setShowQuantity(true)}>
              <i className="fa fa-pencil" /> Quantity
            </button>
          )}
          <button className="ops-btn sm danger" onClick={() => setShowReject(true)}>
            <i className="fa fa-ban" /> Reject
          </button>
        </div>
      </div>

      <div className="ops-steps">
        {steps.map((step, index) => (
          <div
            key={step.key}
            className={`ops-step ${step.done ? 'done' : index === currentIndex ? 'active' : ''}`}
          >
            <div className="dot">{step.done ? <i className="fa fa-check" /> : index + 1}</div>
            <div className="txt">
              <strong>{step.title}</strong>
              <span>{step.hint}</span>
            </div>
          </div>
        ))}
      </div>

      {notEnoughStock && (
        <div className="ops-note error" style={{ marginBottom: 14 }}>
          <i className="fa fa-exclamation-triangle" />
          <span>
            Only <strong>{availableUnits}</strong> unit(s) of {request.productName} are on the shelf,
            but this hand-over needs <strong>{request.quantity}</strong>. Add stock or reduce the quantity.
          </span>
        </div>
      )}

      {current.key === 'otp' && (
        <OtpStep request={request} headers={headers} busy={busy} setBusy={setBusy} onDone={load} />
      )}
      {current.key === 'fingerprint' && (
        <FingerprintStep
          request={request}
          config={config}
          bridge={bridge}
          fingerprint={fingerprint}
          headers={headers}
          busy={busy}
          setBusy={setBusy}
          onDone={load}
          onOverride={() => setShowOverride(true)}
        />
      )}
      {current.key === 'scan' && (
        <ScanStep request={request} product={product} headers={headers} onChange={load} />
      )}
      {current.key === 'confirm' && (
        <ConfirmStep
          request={request}
          detail={detail}
          headers={headers}
          busy={busy}
          setBusy={setBusy}
          disabled={notEnoughStock}
          onDone={onDone}
        />
      )}

      <Timeline events={request.timeline} />

      {showQuantity && (
        <QuantityModal
          request={request}
          headers={headers}
          onClose={() => setShowQuantity(false)}
          onDone={() => { setShowQuantity(false); load(); }}
        />
      )}
      {showReject && (
        <ReasonModal
          title="Reject this request"
          subtitle={`${request.code} · ${request.quantity} × ${request.productName}`}
          label="Why is it being rejected?"
          confirmLabel="Reject request"
          danger
          onClose={() => setShowReject(false)}
          onSubmit={async (reason) => {
            await axios.post(`${api}/api/delivery/admin/request/${request._id}/reject`, { reason }, { headers });
            toast.success('Request rejected.');
            setShowReject(false);
            onDone();
          }}
        />
      )}
      {showOverride && (
        <ReasonModal
          title="Override the fingerprint check"
          subtitle="This is recorded against your name in the delivery log."
          label="Why is the fingerprint being skipped?"
          confirmLabel="Record override"
          minLength={5}
          onClose={() => setShowOverride(false)}
          onSubmit={async (reason) => {
            await axios.post(`${api}/api/delivery/admin/request/${request._id}/override`, { reason }, { headers });
            toast.success('Override recorded.');
            setShowOverride(false);
            load();
          }}
        />
      )}
    </div>
  );
}

// --- Step 1: OTP ------------------------------------------------------------
function OtpStep({ request, headers, busy, setBusy, onDone }) {
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(Boolean(request.verification?.handoverOtpSentAt));
  const [masked, setMasked] = useState('');
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startCountdown = (seconds) => {
    setCountdown(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const send = async (resend = false) => {
    setBusy('otp-send');
    try {
      const { data } = await axios.post(
        `${api}/api/delivery/admin/request/${request._id}/send-otp`,
        { resend },
        { headers }
      );
      setSent(true);
      setMasked(data.maskedPhone || '');
      startCountdown(data.resendAfterSeconds || 60);
      toast.success(data.message);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy('otp-verify');
    try {
      const { data } = await axios.post(
        `${api}/api/delivery/admin/request/${request._id}/verify-otp`,
        { code: code.trim() },
        { headers }
      );
      toast.success(data.message);
      setCode('');
      onDone();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  return (
    <div>
      <div className="ops-note info" style={{ marginBottom: 14 }}>
        <i className="fa fa-mobile" />
        <span>
          {sent
            ? <>A code was sent to <strong>{masked || request.userPhone || 'the customer'}</strong>. Ask them to read it out.</>
            : <>Send a one-time code to the customer's phone, then type what they read back to you.</>}
        </span>
      </div>

      {!sent ? (
        <button className="ops-btn primary lg block" onClick={() => send(false)} disabled={busy === 'otp-send'}>
          {busy === 'otp-send'
            ? <><i className="fa fa-spinner fa-spin" /> Sending…</>
            : <><i className="fa fa-paper-plane" /> Send code to customer</>}
        </button>
      ) : (
        <form onSubmit={verify}>
          <div className="ops-field">
            <label className="ops-label">Code from the customer</label>
            <input
              className="ops-input ops-scan-input"
              inputMode="numeric"
              autoFocus
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
            />
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="submit"
              className="ops-btn primary lg"
              style={{ flex: '1 1 200px' }}
              disabled={code.length < 4 || busy === 'otp-verify'}
            >
              {busy === 'otp-verify'
                ? <><i className="fa fa-spinner fa-spin" /> Checking…</>
                : <><i className="fa fa-check" /> Verify code</>}
            </button>
            <button
              type="button"
              className="ops-btn"
              onClick={() => send(true)}
              disabled={countdown > 0 || busy === 'otp-send'}
            >
              {countdown > 0 ? `Resend in ${countdown}s` : 'Resend'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// --- Step 2: Fingerprint ----------------------------------------------------
function FingerprintStep({ request, config, bridge, fingerprint, headers, busy, setBusy, onDone, onOverride }) {
  const [status, setStatus] = useState('idle'); // idle | scanning | ok | bad
  const [message, setMessage] = useState('');
  const [bridgeState, setBridgeState] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  const attempts = request.verification?.fingerprintAttempts || 0;
  const overrideAvailable = config.allowFingerprintOverride && attempts >= config.maxFingerprintAttempts;

  useEffect(() => {
    let live = true;
    checkBridge(bridge).then((result) => { if (live) setBridgeState(result); });
    return () => { live = false; };
  }, [bridge]);

  const scan = async () => {
    setStatus('scanning');
    setMessage('Ask the customer to place their finger on the scanner…');
    setBusy('fp');
    try {
      // The server hands out a nonce; the bridge signs its verdict with it so a
      // previous "matched" result cannot be replayed from the browser.
      const { data: challenge } = await axios.post(
        `${api}/api/delivery/admin/request/${request._id}/fingerprint/challenge`,
        {},
        { headers }
      );

      const verdict = await verifyFinger(challenge.bridge || bridge, {
        challenge: challenge.challenge,
        templates: challenge.templates,
      });

      const { data } = await axios.post(
        `${api}/api/delivery/admin/request/${request._id}/fingerprint`,
        {
          challenge: challenge.challenge,
          matched: verdict.matched,
          score: verdict.score,
          fingerIndex: verdict.fingerIndex,
          signature: verdict.signature,
          simulated: verdict.simulated,
        },
        { headers }
      );

      setStatus('ok');
      setMessage(data.message);
      toast.success(data.message);
      setTimeout(onDone, 700);
    } catch (err) {
      setStatus('bad');
      const notEnrolled = err.response?.data?.notEnrolled;
      const text = getError(err);
      setMessage(notEnrolled ? 'This customer has no fingerprint on file.' : text);
      if (!notEnrolled) toast.error(text);
      // Refresh so the attempt counter (and the override button) stay accurate.
      if (err.response?.data?.attempts !== undefined) onDone();
    } finally {
      setBusy('');
    }
  };

  const notEnrolled = !fingerprint?.enrolled;

  return (
    <div>
      {bridgeState && !bridgeState.ok && (
        <div className="ops-note warn" style={{ marginBottom: 14 }}>
          <i className="fa fa-plug" />
          <span>
            {bridgeState.message || 'The fingerprint scanner service is not responding.'}
            {config.allowFingerprintOverride && ' You can still override with a reason.'}
          </span>
        </div>
      )}
      {bridge?.simulationMode && (
        <div className="ops-note warn" style={{ marginBottom: 14 }}>
          <i className="fa fa-flask" />
          <span>
            <strong>Simulation mode.</strong> No real scanner is used — every scan will match, and the
            delivery is marked as simulated in the log. Turn this off on the Developer page once the
            SLK20R bridge is installed.
          </span>
        </div>
      )}

      {notEnrolled ? (
        <div className="ops-note error" style={{ marginBottom: 14 }}>
          <i className="fa fa-exclamation-circle" />
          <span>
            <strong>{request.username}</strong> has no enrolled fingerprint.
            {bridge?.allowEnrollAtCounter
              ? ' Enrol them now, or override with a reason.'
              : ' Enrol them from the User list, or override with a reason.'}
          </span>
        </div>
      ) : (
        <p style={{ color: 'var(--ops-muted)', fontSize: 13, marginBottom: 12 }}>
          {fingerprint.fingers} finger{fingerprint.fingers === 1 ? '' : 's'} on file
          {fingerprint.lastMatchedAt && ` · last matched ${new Date(fingerprint.lastMatchedAt).toLocaleDateString()}`}
        </p>
      )}

      <div className={`ops-fp-target ${status}`} style={{ marginBottom: 14 }}>
        <i className={`fa ${status === 'ok' ? 'fa-check-circle' : status === 'bad' ? 'fa-times-circle' : 'fa-hand-paper-o'}`} />
        <div style={{ marginTop: 10, fontSize: 13.5, fontWeight: 600 }}>
          {status === 'idle' && (notEnrolled ? 'No fingerprint on file' : 'Ready to scan')}
          {status === 'scanning' && 'Waiting for the finger…'}
          {status === 'ok' && 'Identity confirmed'}
          {status === 'bad' && 'Not verified'}
        </div>
        {message && (
          <div style={{ marginTop: 5, fontSize: 12.5, color: 'var(--ops-muted)', maxWidth: 420 }}>
            {message}
          </div>
        )}
        {attempts > 0 && status !== 'ok' && (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            <span className="ops-badge amber">
              {attempts} failed attempt{attempts === 1 ? '' : 's'} of {config.maxFingerprintAttempts}
            </span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {!notEnrolled && (
          <button
            className="ops-btn primary lg"
            style={{ flex: '1 1 220px' }}
            onClick={scan}
            disabled={busy === 'fp' || status === 'scanning'}
          >
            {status === 'scanning'
              ? <><i className="fa fa-spinner fa-spin" /> Scanning…</>
              : <><i className="fa fa-hand-paper-o" /> {attempts > 0 ? 'Scan again' : 'Scan fingerprint'}</>}
          </button>
        )}

        {notEnrolled && bridge?.allowEnrollAtCounter && (
          <button className="ops-btn primary lg" style={{ flex: '1 1 220px' }} onClick={() => setEnrolling(true)}>
            <i className="fa fa-user-plus" /> Enrol fingerprint now
          </button>
        )}

        {(overrideAvailable || notEnrolled || (bridgeState && !bridgeState.ok)) && config.allowFingerprintOverride && (
          <button className="ops-btn" onClick={onOverride}>
            <i className="fa fa-unlock-alt" /> Override with reason
          </button>
        )}
      </div>

      {enrolling && (
        <EnrollModal
          user={{ _id: request.user, username: request.username }}
          bridge={bridge}
          headers={headers}
          onClose={() => setEnrolling(false)}
          onDone={() => { setEnrolling(false); onDone(); }}
        />
      )}
    </div>
  );
}

// --- Step 3: Scan units -----------------------------------------------------
function ScanStep({ request, product, headers, onChange }) {
  const [serial, setSerial] = useState('');
  const [working, setWorking] = useState(false);
  const inputRef = useRef(null);

  // Barcode scanners behave like keyboards, so the field has to keep focus
  // between scans or the next read goes nowhere.
  useEffect(() => {
    inputRef.current?.focus();
  }, [request.scannedSerials.length]);

  const scanned = request.scannedSerials || [];
  const remaining = request.quantity - scanned.length;

  const submit = async (e) => {
    e.preventDefault();
    const value = serial.trim();
    if (!value || working) return;
    setWorking(true);
    try {
      await axios.post(`${api}/api/delivery/admin/request/${request._id}/scan`, { serial: value }, { headers });
      setSerial('');
      await onChange();
    } catch (err) {
      toast.error(getError(err));
      setSerial('');
    } finally {
      setWorking(false);
      inputRef.current?.focus();
    }
  };

  const remove = async (value) => {
    try {
      await axios.delete(
        `${api}/api/delivery/admin/request/${request._id}/scan/${encodeURIComponent(value)}`,
        { headers }
      );
      await onChange();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  return (
    <div>
      <div className="ops-note info" style={{ marginBottom: 14 }}>
        <i className="fa fa-barcode" />
        <span>
          Scan the sticker on each unit you are handing over.{' '}
          <strong>{remaining}</strong> of {request.quantity} still to scan.
          {product && ` ${product.available} available on the shelf.`}
        </span>
      </div>

      <form onSubmit={submit}>
        <label className="ops-label">Scan or type a serial</label>
        <input
          ref={inputRef}
          className="ops-input ops-scan-input"
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
          placeholder={remaining > 0 ? 'Scan the sticker…' : 'All units scanned'}
          disabled={remaining <= 0 || working}
          autoComplete="off"
        />
      </form>

      {scanned.length > 0 && (
        <div className="ops-chips">
          {scanned.map((value) => (
            <span key={value} className="ops-chip">
              {value}
              <button type="button" onClick={() => remove(value)} title="Remove">×</button>
            </span>
          ))}
        </div>
      )}

      {remaining <= 0 && (
        <div className="ops-note ok" style={{ marginTop: 14 }}>
          <i className="fa fa-check-circle" />
          <span>All {request.quantity} unit(s) scanned. Move to the final step to hand them over.</span>
        </div>
      )}
    </div>
  );
}

// --- Step 4: Confirm --------------------------------------------------------
function ConfirmStep({ request, detail, headers, busy, setBusy, disabled, onDone }) {
  const v = request.verification || {};
  const { config } = detail;

  const complete = async () => {
    setBusy('complete');
    try {
      const { data } = await axios.post(
        `${api}/api/delivery/admin/request/${request._id}/complete`,
        {},
        { headers }
      );
      toast.success(data.message);
      onDone();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  const checks = [
    config.handoverOtpEnabled && {
      label: 'OTP verified',
      ok: v.otpVerified,
      detail: v.otpVerifiedAt ? new Date(v.otpVerifiedAt).toLocaleTimeString() : '',
    },
    config.requireFingerprint && {
      label: v.overridden ? 'Fingerprint overridden' : 'Fingerprint verified',
      ok: v.fingerprintVerified || v.overridden,
      warn: v.overridden || v.fingerprintSimulated,
      detail: v.overridden
        ? v.overrideReason
        : v.fingerprintSimulated
        ? 'Simulated — no hardware'
        : v.fingerprintScore
        ? `score ${v.fingerprintScore}`
        : '',
    },
    config.requireSerialScan && {
      label: `${request.scannedSerials.length} of ${request.quantity} units scanned`,
      ok: request.scannedSerials.length >= request.quantity,
      detail: request.scannedSerials.join(', '),
    },
  ].filter(Boolean);

  const allOk = checks.every((c) => c.ok);

  return (
    <div>
      <div className="ops-card" style={{ background: '#f8fafc', marginBottom: 14 }}>
        {checks.map((check) => (
          <div
            key={check.label}
            style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 0' }}
          >
            <i
              className={`fa ${check.ok ? 'fa-check-circle' : 'fa-circle-o'}`}
              style={{ color: check.ok ? (check.warn ? 'var(--ops-amber)' : 'var(--ops-green)') : '#cbd5e1', marginTop: 2 }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{check.label}</div>
              {check.detail && (
                <div className="ops-mono" style={{ color: 'var(--ops-muted)', fontSize: 11.5, wordBreak: 'break-all' }}>
                  {check.detail}
                </div>
              )}
            </div>
          </div>
        ))}
        {!checks.length && (
          <p style={{ margin: 0, color: 'var(--ops-muted)', fontSize: 13 }}>
            No verification steps are switched on — this hands over straight away.
          </p>
        )}
      </div>

      {!config.requireSerialScan && (
        <div className="ops-note info" style={{ marginBottom: 14 }}>
          <i className="fa fa-info-circle" />
          <span>Serial scanning is off, so the oldest {request.quantity} unit(s) on the shelf will be assigned automatically.</span>
        </div>
      )}

      <button
        className="ops-btn success lg block"
        onClick={complete}
        disabled={!allOk || disabled || busy === 'complete'}
      >
        {busy === 'complete'
          ? <><i className="fa fa-spinner fa-spin" /> Handing over…</>
          : <><i className="fa fa-check" /> Hand over {request.quantity} × {request.productName}</>}
      </button>
    </div>
  );
}

// --- Shared pieces ----------------------------------------------------------
function Timeline({ events }) {
  if (!events?.length) return null;
  const tone = (action) =>
    action.includes('failed') || action.includes('reject') ? 'bad'
      : action.includes('verified') || action === 'completed' ? 'ok'
      : '';

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--ops-line)' }}>
      <h3 style={{ fontSize: 14, margin: '0 0 12px' }}>What has happened so far</h3>
      <ul className="ops-timeline">
        {[...events].reverse().map((event, index) => (
          <li key={index} className={tone(event.action)}>
            <div className="when">
              {new Date(event.at).toLocaleString()}{event.by ? ` · ${event.by}` : ''}
            </div>
            <div className="what">{event.action.replace(/_/g, ' ')}</div>
            {event.detail && <div className="detail">{event.detail}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuantityModal({ request, headers, onClose, onDone }) {
  const [quantity, setQuantity] = useState(String(request.quantity));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${api}/api/delivery/admin/request/${request._id}/quantity`,
        { quantity: Number(quantity) },
        { headers }
      );
      toast.success(data.message);
      onDone();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ops-overlay" onClick={onClose}>
      <form className="ops-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="ops-modal-head">
          <div>
            <h4>Change the quantity</h4>
            <p className="sub">{request.code} · {request.productName}</p>
          </div>
          <button type="button" className="ops-x" onClick={onClose}><i className="fa fa-times" /></button>
        </div>
        <div className="ops-modal-body">
          <div className="ops-field">
            <label className="ops-label">Units to hand over</label>
            <input
              className="ops-input"
              type="number"
              min="1"
              autoFocus
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
          <div className="ops-note info">
            <i className="fa fa-info-circle" />
            <span>
              The customer asked for {request.originalQuantity}. Anything not handed over now stays
              on their order and can be collected later.
            </span>
          </div>
        </div>
        <div className="ops-modal-foot">
          <button type="button" className="ops-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="ops-btn primary" disabled={saving || !Number(quantity)}>
            {saving ? 'Saving…' : 'Update quantity'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ReasonModal({ title, subtitle, label, confirmLabel, minLength = 1, danger, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit(reason.trim());
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ops-overlay" onClick={onClose}>
      <form className="ops-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="ops-modal-head">
          <div>
            <h4>{title}</h4>
            {subtitle && <p className="sub">{subtitle}</p>}
          </div>
          <button type="button" className="ops-x" onClick={onClose}><i className="fa fa-times" /></button>
        </div>
        <div className="ops-modal-body">
          <label className="ops-label">{label}</label>
          <textarea
            className="ops-textarea"
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Be specific — this is kept in the audit log."
          />
        </div>
        <div className="ops-modal-foot">
          <button type="button" className="ops-btn" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className={`ops-btn ${danger ? 'danger' : 'primary'}`}
            disabled={reason.trim().length < minLength || saving}
          >
            {saving ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

// Enrollment, reachable both here and from the User list.
export function EnrollModal({ user, bridge, headers, onClose, onDone }) {
  const total = bridge?.fingersToEnroll || 2;
  const [captures, setCaptures] = useState([]);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const FINGER_LABELS = ['Right thumb', 'Right index', 'Left thumb', 'Left index'];

  const capture = async () => {
    const index = captures.length + 1;
    setStatus('scanning');
    setMessage(`Place the ${(FINGER_LABELS[captures.length] || `finger ${index}`).toLowerCase()} on the scanner…`);
    try {
      const result = await captureFinger(bridge, {
        index,
        label: FINGER_LABELS[captures.length] || `Finger ${index}`,
      });
      setCaptures((list) => [
        ...list,
        { ...result, index, label: FINGER_LABELS[list.length] || `Finger ${index}` },
      ]);
      setStatus('ok');
      setMessage(`Captured (quality ${result.quality}).`);
    } catch (err) {
      setStatus('bad');
      setMessage(err.message);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: challenge } = await axios.post(
        `${api}/api/fingerprint/enroll/challenge`,
        { userId: user._id },
        { headers }
      );
      const { data } = await axios.post(
        `${api}/api/fingerprint/enroll`,
        { userId: user._id, challenge: challenge.challenge, fingers: captures },
        { headers }
      );
      toast.success(data.message);
      onDone();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ops-overlay" onClick={onClose}>
      <div className="ops-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ops-modal-head">
          <div>
            <h4>Enrol fingerprint</h4>
            <p className="sub">{user.username} · {captures.length} of {total} captured</p>
          </div>
          <button className="ops-x" onClick={onClose}><i className="fa fa-times" /></button>
        </div>

        <div className="ops-modal-body">
          {bridge?.simulationMode && (
            <div className="ops-note warn" style={{ marginBottom: 14 }}>
              <i className="fa fa-flask" />
              <span>
                <strong>Simulation mode.</strong> These captures are placeholders, not real fingerprints.
                Re-enrol once the scanner bridge is installed.
              </span>
            </div>
          )}

          <div className={`ops-fp-target ${status}`}>
            <i className={`fa ${status === 'ok' ? 'fa-check-circle' : status === 'bad' ? 'fa-times-circle' : 'fa-hand-paper-o'}`} />
            <div style={{ marginTop: 10, fontSize: 13.5, fontWeight: 600 }}>
              {captures.length >= total
                ? 'All fingers captured'
                : `Capture ${FINGER_LABELS[captures.length] || `finger ${captures.length + 1}`}`}
            </div>
            {message && <div style={{ marginTop: 5, fontSize: 12.5, color: 'var(--ops-muted)' }}>{message}</div>}
          </div>

          {captures.length > 0 && (
            <div className="ops-chips">
              {captures.map((c) => (
                <span key={c.index} className="ops-chip">
                  {c.label} · Q{c.quality}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="ops-modal-foot">
          <button className="ops-btn" onClick={onClose}>Cancel</button>
          {captures.length < total ? (
            <button className="ops-btn primary" onClick={capture} disabled={status === 'scanning'}>
              {status === 'scanning'
                ? <><i className="fa fa-spinner fa-spin" /> Waiting…</>
                : <><i className="fa fa-hand-paper-o" /> Capture finger {captures.length + 1}</>}
            </button>
          ) : (
            <button className="ops-btn success" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : <><i className="fa fa-check" /> Save enrollment</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
